import type { ReactNode } from "react";
import {
  AlertTriangle,
  CircleCheckBig,
  Info,
  TriangleAlert,
} from "lucide-react";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { cn } from "../../app/components/primitives/utils";

// The portal's loading, failed and empty states, shared by every page so they
// read the same everywhere.

export function LoadingCard({ what }: { what: string }) {
  return (
    <Card
      role="status"
      className="p-8 text-center text-sm text-muted-foreground"
    >
      Loading {what}…
    </Card>
  );
}

export function LoadFailed({
  what,
  message,
  onRetry,
}: {
  what: string;
  message: string;
  onRetry: () => void;
}) {
  return (
    <Card role="alert" className="items-center gap-3 p-8 text-center">
      <AlertTriangle className="size-6 text-muted-foreground" aria-hidden />
      <p className="font-medium">We couldn&apos;t load {what}</p>
      <p className="text-sm text-muted-foreground">{message}</p>
      <Button variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </Card>
  );
}

/** A plain centred message: link no longer valid, not found, nothing here. */
export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="grid justify-items-center gap-2 px-5 py-10 text-center text-sm text-muted-foreground">
      <h1 className="text-base font-semibold text-foreground">{title}</h1>
      {children}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** A section heading in the mock-ups' small-caps style: "LINES", "INVOICE", "ACTIVITY". */
export function SectionHeading({
  id,
  children,
}: {
  id?: string;
  children: ReactNode;
}) {
  return (
    <h2
      id={id}
      className="border-b pb-1.5 text-[11px] font-bold tracking-wider text-muted-foreground uppercase"
    >
      {children}
    </h2>
  );
}

const BANNER_TONES = {
  info: {
    Icon: Info,
    box: "bg-blue-50 dark:bg-blue-950/40",
    icon: "text-blue-700 dark:text-blue-300",
  },
  warn: {
    Icon: TriangleAlert,
    box: "bg-amber-50 dark:bg-amber-950/40",
    icon: "text-amber-700 dark:text-amber-300",
  },
  ok: {
    Icon: CircleCheckBig,
    box: "bg-green-50 dark:bg-green-950/40",
    icon: "text-green-700 dark:text-green-300",
  },
} as const;

/** A one-line notice at the top of a page: blue for information, amber for a warning, green for done. */
export function Banner({
  tone,
  children,
}: {
  tone: keyof typeof BANNER_TONES;
  children: ReactNode;
}) {
  const { Icon, box, icon } = BANNER_TONES[tone];
  return (
    <div
      role={tone === "ok" ? "status" : undefined}
      className={cn(
        "flex items-start gap-2.5 rounded-lg px-3.5 py-2.5 text-sm",
        box,
      )}
    >
      <Icon aria-hidden className={cn("mt-0.5 size-4 shrink-0", icon)} />
      <span>{children}</span>
    </div>
  );
}
